/**
 * SystemSectionPagesV2 — Phase 11 of the Rebirth migration.
 *
 * Round 5 upgrade: replace placeholders with first-pass real content for
 * Intelligence Center + Feature Flags.
 */
import React from 'react';
import { Brain, Flag, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isRebirthSystemEnabled } from '../utils/featureFlags';
import IntelligenceCenterPage from './IntelligenceCenterPage';
import FeatureFlagsPage from './FeatureFlagsPage';
import { V2PageShell, Button, Pill, Card, MetricCard, Table, PostureBanner } from '../components/v2';

/* ═══════════════════════════════════════════════════════════════════
   INTELLIGENCE CENTER — cross-tenant analytics
   ─────────────────────────────────────────────────────────────────── */
function IntelligenceCenterPageV2Inner() {
  const navigate = useNavigate();
  return (
    <V2PageShell
      title="Intelligence Center"
      subtitle="System-level intelligence · advanced analytics · Super Admin only"
      tags={<Pill tone="trust" size="sm">System</Pill>}
    >
      <div className="space-y-5">
        <PostureBanner
          score={96}
          title="Cross-tenant posture: Strong — 12 tenants, 184 agents, 1.2M decisions / 30d"
          message={<>Self-inference 91.2% · model spend <strong className="text-v2-ink">$8,412 / mo</strong> · zero cross-tenant data leakage events.</>}
        />
        <div className="grid sm:grid-cols-4 gap-4">
          <MetricCard label="Tenants"          value="12"   tone="cyan"    delta={{ value: '+1 this month', direction: 'up', intent: 'positive' }} />
          <MetricCard label="Agents (system)"  value="184"  tone="trust"   delta={{ value: '+22 this month', direction: 'up', intent: 'positive' }} />
          <MetricCard label="Decisions / 30d"  value="1.2M" tone="success" delta={{ value: '+8%', direction: 'up', intent: 'neutral' }} />
          <MetricCard label="Model spend / mo" value="$8,412" tone="warning" delta={{ value: '+11%', direction: 'up', baseline: 'vs. last month', intent: 'negative' }} />
        </div>
        <Card>
          <Card.Header title="Top tenants by activity" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/intelligence')}>Open full page</Button>} />
          <Card.Body>
            <Table
              ariaLabel="Top tenants"
              density="compact"
              columns={[
                { key: 'tenant',  header: 'Tenant', render: (r) => <strong className="text-v2-ink">{r.tenant}</strong> },
                { key: 'agents',  header: 'Agents', render: (r) => <span className="tabular-nums">{r.agents}</span> },
                { key: 'decs',    header: 'Decisions / 30d', render: (r) => <span className="tabular-nums">{r.decs.toLocaleString()}</span> },
                { key: 'spend',   header: 'Spend / mo', render: (r) => <span className="tabular-nums font-mono-v2 text-[11.5px]">${r.spend.toLocaleString()}</span> },
                { key: 'health',  header: 'Health', render: (r) => <Pill tone={r.health === 'healthy' ? 'success' : 'warning'} dot size="sm">{r.health}</Pill> },
              ]}
              rows={[
                { tenant: 'acme-prod',     agents: 42, decs: 312000, spend: 2840, health: 'healthy' },
                { tenant: 'globex-prod',   agents: 28, decs: 218000, spend: 1920, health: 'healthy' },
                { tenant: 'initech-prod',  agents: 22, decs: 184000, spend: 1612, health: 'healthy' },
                { tenant: 'acme-staging',  agents:  8, decs:  41000, spend:  348, health: 'healthy' },
                { tenant: 'initech-dev',   agents:  4, decs:  18000, spend:  180, health: 'healthy' },
              ]}
              rowKey={(r) => r.tenant}
            />
          </Card.Body>
        </Card>
        <Card>
          <Card.Header title="System intelligence chat" actions={<Pill tone="trust" size="sm">beta</Pill>} />
          <Card.Body>
            <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
              Ask "Why did Aria Threat demote to SUPERVISED yesterday?" or "Which tenants spent the most on inference this week?" — the system intelligence agent walks the audit trail + protection verdicts to answer.
            </p>
            <Button variant="primary" size="sm" className="mt-3">Open chat</Button>
          </Card.Body>
        </Card>
      </div>
    </V2PageShell>
  );
}
export function ResolvedIntelligenceCenterPage(props) {
  /* G88 — render v1 unconditionally. IntelligenceCenterPage owns the
   * full operator chat + AI model configuration surfaces. */
  return <IntelligenceCenterPage {...props} />;
  // eslint-disable-next-line no-unreachable
  if (isRebirthSystemEnabled()) return <IntelligenceCenterPageV2Inner {...props} />;
}

/* ═══════════════════════════════════════════════════════════════════
   FEATURE FLAGS — categorised toggle list
   ─────────────────────────────────────────────────────────────────── */
function FeatureFlagsPageV2Inner() {
  const navigate = useNavigate();
  return (
    <V2PageShell
      title="Feature Flags"
      subtitle="Cluster + tenant flag controls · always reachable for Super Admins"
      tags={<Pill tone="trust" size="sm">System</Pill>}
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-4 gap-4">
          <MetricCard label="Total flags" value="48" tone="cyan" />
          <MetricCard label="UI feature flags" value="12 / 12 ON" tone="success" />
          <MetricCard label="AI features" value="6 / 8 ON" tone="trust" />
          <MetricCard label="Experimental" value="3 ON" tone="warning" />
        </div>
        <Card>
          <Card.Header title="UI feature flags" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/flags')}>Open the full flag editor</Button>} />
          <Card.Body>
            <Table
              ariaLabel="UI feature flags"
              density="compact"
              columns={[
                { key: 'key',   header: 'Flag', render: (r) => <code className="font-mono-v2 text-[11px] text-v2-ink-secondary">{r.key}</code> },
                { key: 'desc',  header: 'Description', render: (r) => <span className="text-v2-ink-secondary">{r.desc}</span> },
                { key: 'state', header: 'State', render: (r) => <Pill tone={r.state === 'on' ? 'success' : 'muted'} dot size="sm">{r.state}</Pill> },
              ]}
              rows={[
                { key: 'features.rebirth.shell.v2',                desc: 'Navy sidebar shell + topbar + breadcrumbs', state: 'on' },
                { key: 'features.rebirth.overview.v2',             desc: 'Outcomes-first Overview page',              state: 'on' },
                { key: 'features.rebirth.approvals.v2',            desc: 'v2 Approvals queue with ApprovalCard',      state: 'on' },
                { key: 'features.rebirth.agentlets-inventory.v2',  desc: 'v2 inventory + slideout',                   state: 'on' },
                { key: 'features.rebirth.agentlet-detail.v2',      desc: 'v2 detail page + B-D-A tabs',               state: 'on' },
                { key: 'features.rebirth.discovery.v2',            desc: 'v2 Discovery (Agents + Workflows)',         state: 'on' },
                { key: 'features.rebirth.governance.v2',           desc: 'v2 Governance Center + 5 subviews',         state: 'on' },
                { key: 'features.rebirth.policies.v2',             desc: 'v2 Policies (8 tabs)',                      state: 'on' },
                { key: 'features.rebirth.skill-center.v2',         desc: 'v2 Skill Center fleet view',                state: 'on' },
                { key: 'features.rebirth.build-section.v2',        desc: 'v2 Connectors / Marketplace / Templates',   state: 'on' },
                { key: 'features.rebirth.administer.v2',           desc: 'v2 Admin Center / Team / Deployments',      state: 'on' },
                { key: 'features.rebirth.system.v2',               desc: 'v2 Intelligence Center + Flags',            state: 'on' },
              ]}
              rowKey={(r) => r.key}
            />
          </Card.Body>
        </Card>
      </div>
    </V2PageShell>
  );
}
export function ResolvedFeatureFlagsPage(props) {
  /* G88 — render v1 unconditionally. FeatureFlagsPage owns the full
   * cluster + tenant flag editor with role-aware controls. */
  return <FeatureFlagsPage {...props} />;
  // eslint-disable-next-line no-unreachable
  if (isRebirthSystemEnabled()) return <FeatureFlagsPageV2Inner {...props} />;
}
